import { CreateButton, useTable } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';
import { Button, Space, Table, Tag, Tooltip } from 'antd';

import {
  IResourceComponentsProps,
  useGetIdentity,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';

import { EditOutlined } from '@ant-design/icons';
import {
  baseEmotionsEn,
  baseEmotionsRu,
  baseFeelingsEn,
  baseFeelingsRu,
  dbCols,
  UserRole,
} from '@cc/schema';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { IUser } from '../../providers';
import { AbcModel } from '../../schema/be-graphql-generated';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useMemo } from 'react';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import useStyles from './styles.style';

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const AbcModelsList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();

  const { styles } = useStyles();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  const { i18n } = useTranslation();

  const getLocale = useGetLocale();

  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  const baseEmotions = useMemo(() => {
    const baseEmotions = currentLocale?.startsWith('ru')
      ? baseEmotionsRu
      : baseEmotionsEn;

    return baseEmotions.map((e) => ({
      ...e,
      children: e.children.map((ch) => ({ ...ch, strength: 50 })),
    }));
  }, [currentLocale]);

  const flattenedEmotions = useMemo(() => {
    return baseEmotions.reduce(
      (acc, val) => [...acc, ...val.children],
      [] as (typeof baseEmotionsEn)[0]['children'],
    );
  }, [baseEmotions]);

  const baseFeelings = currentLocale?.startsWith('ru')
    ? baseFeelingsRu
    : baseFeelingsEn;
  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  // @ts-ignore
  const { tableProps, setFilters } = useTable<AbcModel>({
    // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
    resource: 'abcModels',
    queryOptions: {
      select: (data) => {
        // @ts-ignore
        return { data: data.data.data, total: data.total };
      },
    },

    meta: {
      operation: 'abcModels',
      fields: [
        {
          data: [
            '_id',
            'userId',
            'createdAt',
            'updatedAt',
            'a',
            'b',
            'behaviors',
            'feelings',
            { emotions: ['id', 'strength'] },
          ],
        },
        'count',
      ],
    },

    filters: {
      initial: clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
    },

    sorters: {
      initial: [
        {
          field: 'updatedAt',
          order: 'desc',
        },
      ],
    },
  });

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
      'replace',
    );
  }, [clientFilterId]);

  useActivityTracking(dbCols.abcModels, fixedClientId, !fixedClientId);

  const flattenedFeelings = useMemo(() => {
    return baseFeelings.reduce(
      (acc, val) => [...acc, ...val.children],
      [] as (typeof baseFeelings)[0]['children'],
    );
  }, [baseFeelings]);

  // the WHOLE practice roster (P011) — the client column, the client filter and the edit gate below
  // all have to be able to name the owner of every row this table can render
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('shared.abcModels.title')}
      headerButtons={
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {fixedClientId ? null : (
            <ShowNamesButton
              visible={names.visible}
              onToggle={names.toggle}
              testId="cc-abc-models-toggle-names"
            />
          )}
          <CreateButton
            data-testid="create-abc-model-button"
            onClick={
              fixedClientId
                ? () => navigateTo(`/tools/abcModels/create?clientId=${encodeURIComponent(fixedClientId)}`)
                : undefined
            }
          />
        </div>
      }
    >
      <Table
        {...embeddedTableProps(fixedClientId)}
        {...tableProps}
        rowKey="_id"
        onChange={(pagination, filters, sorter, extra) => {
          // Note: The data we get from Ant.design filters does not match the
          // react refine one. Currently it works fine if I simply silence it the way below.
          // TOOD: Find a way to convert the data to match the react refine one and fix it better
          // @ts-ignore
          tableProps?.onChange?.(pagination, null, sorter, extra);
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        data-testid="abc-models-list"
      >
        {fixedClientId ? null : (
        <Table.Column
          key="_id"
          dataIndex="userId"
          title={translate('resourcesCommon.userId')}
          sorter
          filterSearch
          // @ts-ignore
          onFilter={(value: string, record: AbcModel) => {
            return record.userId === value;
          }}
          filters={clients.map((c) => {
            return {
              text: clientFilterText(c, names.visible),
              value: c._id,
            };
          })}
          render={(value, record) => {
            const client = clients.find((c) => c._id === value);

            return (
              <ClientName
                id={value}
                firstName={client?.firstName}
                lastName={client?.lastName}
                visible={names.visible}
              />
            );
          }}
        />
        )}

        <Table.Column<AbcModel>
          key="a"
          dataIndex="a"
          title={translate('shared.abcModel.a')}
        />

        <Table.Column<AbcModel>
          key="b"
          dataIndex="b"
          title={translate('shared.abcModel.b')}
        />

        <Table.Column<AbcModel>
          key="c"
          dataIndex="c"
          title={translate('shared.abcModel.c')}
          render={(text, record, indx) => {
            return (
              <div>
                <div className={styles['abc-models--consequences-row']}>
                  <div className={styles['abc-models--consequences-row-label']}>
                    {translate('shared.abcModel.emotions')}:
                  </div>
                  <div className={styles['abc-models--consequences-row-value']}>
                    {(record?.emotions || []).map((re) => (
                      <Tag key={re?.id}>
                        {flattenedEmotions.find((e) => e.id === re?.id)?.name ||
                          ''}
                      </Tag>
                    ))}
                  </div>
                </div>

                <div className={styles['abc-models--consequences-row']}>
                  <div className={styles['abc-models--consequences-row-label']}>
                    {translate('shared.abcModel.behaviors')}:
                  </div>
                  <div className={styles['abc-models--consequences-row-value']}>
                    {' '}
                    {record?.behaviors || ''}
                  </div>
                </div>

                <div className={styles['abc-models--consequences-row']}>
                  <div className={styles['abc-models--consequences-row-label']}>
                    {translate('shared.abcModel.sensations')}:
                  </div>
                  <div className={styles['abc-models--consequences-row-value']}>
                    {(record?.feelings || []).map((f) => (
                      <Tag key={f}>
                        {flattenedFeelings.find((bf) => bf.id === f)?.name ||
                          ''}
                      </Tag>
                    ))}
                  </div>
                </div>
              </div>
            );
          }}
        />

        <Table.Column
          key="updatedAt"
          dataIndex="updatedAt"
          title={translate('resourcesCommon.updatedAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm'
                : 'yyyy-MM-dd hh:mm aaa',
            );
          }}
        />

        <Table.Column<AbcModel>
          title={translate('resourcesCommon.actions')}
          dataIndex="actions"
          render={(_, record) => {
            const client = clients.find((c) => c._id === record.userId);

            const hasPremiumAccess = client?.accessLevel;

            const canEdit =
              client?.access?.allowEdit ||
              (client?.access?.allowAdminEdit &&
                identity?.db?.role === UserRole.THERAPIST_ADMIN);

            return (
              <Space>
                {canEdit && (
                  <Tooltip
                    title={translate(
                      hasPremiumAccess
                        ? 'resourcesCommon.edit'
                        : 'clients.thoughtsTestDisabledTip',
                    )}
                  >
                    <Link
                      to={`/tools/abcModels/create?abcModelId=${record._id}`}
                    >
                      <Button
                        icon={<EditOutlined />}
                        disabled={!hasPremiumAccess || !canEdit}
                      />
                    </Link>
                  </Tooltip>
                )}
              </Space>
            );
          }}
        />
      </Table>
    </ToolList>
  );
};
